// Maximum number of rounds allowed in the interview setup flow.
export const MAX_ROUNDS_LIMIT = 2
export const HR_PHONE_COUNTRY_CODE = "+91"

export const DEFAULT_SCHEDULER_INTERVIEW_DAYS = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
]

export const isJobDraftStatus = (jobStatus?: string | null) => !jobStatus || jobStatus === "DRAFT"

export const INTERVIEW_SCHEDULING_TASK_TYPE = "INTERVIEW_SCHEDULING" as const
export const SCREENING_TASK_TYPE = "SCREENING" as const

export const SCHEDULER_ROUND_DELETE_BLOCKED_MESSAGE =
    "Scheduling rounds cannot be deleted on an active job."

export const DUPLICATE_SCHEDULER_ROUND_MESSAGE =
    "Only one Interview Scheduling task is allowed per job."

export const isSchedulerRoundDeletionBlocked = (
    jobStatus?: string | null,
    taskType?: string | null
) => !isJobDraftStatus(jobStatus) && taskType === INTERVIEW_SCHEDULING_TASK_TYPE

export const isSchedulerRoundReadOnly = isSchedulerRoundDeletionBlocked

export const hasSchedulerRound = (rounds: Array<{ taskType?: string | null }>) =>
    rounds.some((round) => round.taskType === INTERVIEW_SCHEDULING_TASK_TYPE)

export const countSchedulerRounds = (rounds: Array<{ taskType?: string | null }>) =>
    rounds.filter((round) => round.taskType === INTERVIEW_SCHEDULING_TASK_TYPE).length

export const hasDuplicateSchedulerRounds = (rounds: Array<{ taskType?: string | null }>) =>
    countSchedulerRounds(rounds) > 1

const isInterviewSchedulingAddOnEnabled = (value: boolean | string | undefined) =>
    value === true || value === "ENABLED"

type HybridPersistRound = {
    taskType?: string | null
    isSingleCallSchedulerRound?: boolean
    schedulerDetails?: unknown
    roundPlanAddOn?: {
        interviewSchedulingEnabled?: boolean | string
    }
}

export const shouldOmitHybridSchedulerRoundFromPersist = (
    round: HybridPersistRound,
    persistContext: {
        completeRoundsInSingleCall?: boolean
        rounds?: HybridPersistRound[]
    }
) => {
    if (round.taskType !== INTERVIEW_SCHEDULING_TASK_TYPE) {
        return false
    }

    if (round.isSingleCallSchedulerRound) {
        return true
    }

    if (persistContext.completeRoundsInSingleCall) {
        return true
    }

    return (persistContext.rounds ?? []).some(
        (candidateRound) =>
            candidateRound.taskType === SCREENING_TASK_TYPE &&
            isInterviewSchedulingAddOnEnabled(candidateRound.roundPlanAddOn?.interviewSchedulingEnabled) &&
            !!candidateRound.schedulerDetails
    )
}

const GOOGLE_MAPS_HOSTNAMES = new Set(["maps.app.goo.gl", "maps.google.com", "www.google.com", "google.com"])

const parseSchedulerDateValue = (dateValue?: string | null) => {
    if (!dateValue) return null

    const [yearValue, monthValue, dayValue] = dateValue.split("-").map(Number)
    if (!yearValue || !monthValue || !dayValue) return null

    return new Date(yearValue, monthValue - 1, dayValue)
}

const schedulerWeekdayValues = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
] as const

export const getSchedulerInterviewDaysInRange = (
    startDate?: string | null,
    endDate?: string | null
): string[] => {
    const parsedStartDate = parseSchedulerDateValue(startDate)
    const parsedEndDate = parseSchedulerDateValue(endDate)
    if (!parsedStartDate || !parsedEndDate) return []
    if (parsedEndDate.getTime() < parsedStartDate.getTime()) return []

    const availableInterviewDays = new Set<string>()
    const currentDate = new Date(parsedStartDate)

    while (currentDate.getTime() <= parsedEndDate.getTime()) {
        availableInterviewDays.add(schedulerWeekdayValues[currentDate.getDay()])
        currentDate.setDate(currentDate.getDate() + 1)
    }

    return DEFAULT_SCHEDULER_INTERVIEW_DAYS.filter((interviewDay) => availableInterviewDays.has(interviewDay))
}

export const parseSchedulerTimeToMinutes = (timeValue?: string | null) => {
    if (!timeValue?.trim()) return null

    const [hourValue, minuteValue] = timeValue.split(":").map(Number)
    if (Number.isNaN(hourValue) || Number.isNaN(minuteValue)) return null

    return hourValue * 60 + minuteValue
}

export const isSchedulerDateRangeValid = (startDate?: string | null, endDate?: string | null) => {
    if (!startDate?.trim() || !endDate?.trim()) return false

    const parsedStartDate = parseSchedulerDateValue(startDate)
    const parsedEndDate = parseSchedulerDateValue(endDate)
    if (!parsedStartDate || !parsedEndDate) return false

    return parsedEndDate.getTime() >= parsedStartDate.getTime()
}

export const isSchedulerDailyTimeWindowValid = (startTime?: string | null, endTime?: string | null) => {
    const startTimeMinutes = parseSchedulerTimeToMinutes(startTime)
    const endTimeMinutes = parseSchedulerTimeToMinutes(endTime)
    if (startTimeMinutes === null || endTimeMinutes === null) return false

    return endTimeMinutes > startTimeMinutes
}

export const isSchedulerTimeAfter = (timeValue: string, minTimeValue?: string | null) => {
    const timeMinutes = parseSchedulerTimeToMinutes(timeValue)
    const minTimeMinutes = parseSchedulerTimeToMinutes(minTimeValue)
    if (timeMinutes === null || minTimeMinutes === null) return true

    return timeMinutes > minTimeMinutes
}

export const isValidGoogleMapsUrl = (mapsUrl?: string | null) => {
    const trimmedMapsUrl = mapsUrl?.trim()
    if (!trimmedMapsUrl) return false

    try {
        const parsedMapsUrl = new URL(trimmedMapsUrl)
        const isHttpUrl = parsedMapsUrl.protocol === "https:" || parsedMapsUrl.protocol === "http:"
        if (!isHttpUrl || !GOOGLE_MAPS_HOSTNAMES.has(parsedMapsUrl.hostname)) return false

        return parsedMapsUrl.hostname === "maps.app.goo.gl" || parsedMapsUrl.pathname.startsWith("/maps")
    } catch {
        return false
    }
}
